import {
  ConfirmationDialog,
  DetailColumns,
  DetailPane,
  FormDialog,
  fullDateTime,
  name,
  useDetail,
  useDialogs,
  useErrors
} from '@management-ui/core';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash';
import {reverse} from 'named-urls';
import * as React from 'react';
import {useCallback, useContext, useMemo} from 'react';
import {ServiceContext} from '../../../../components/Services';
import routes from '../../../../routes';
import UserForm from '../../forms/UserForm';
import Wrapper from './Wrapper';
import Tabs from './Tabs';
import YesNo from '../../../../components/YesNo';

const Administrator = ({history, archive = false}) => {
  const services = useContext(ServiceContext);

  const {errors, toggleError} = useErrors(useMemo(() => ({
    user: 'Sorry the user could not be accessed',
    activity: 'Sorry the user activity could not be loaded'
  }), []));

  const {
    entity: user,
    setEntity: setUser,
    loadEntity: loadUser,
    crumbs,
    loading,
    setLoading
  } = useDetail(
    services.user.getUser,
    archive,
    useCallback((user) => [{title: name(user)}], []),
    useMemo(() => Wrapper.standardCrumbs, []),
    useMemo(() => Wrapper.archiveCrumbs, []),
    useCallback((displayError) => toggleError('user', displayError), [toggleError])
  );

  const {openDialogs, toggleDialog} = useDialogs(['edit', 'delete']);

  const handleSaved = useCallback((updated) => {
    toggleDialog('edit', false);
    if (updated) {
      setUser(updated);
    } else if (user?.id) {
      loadUser(user.id)
    }
  }, [toggleDialog, setUser, loadUser, user]);

  const handleRestore = useCallback(() => {
    setLoading(true);
    services.user.restoreUser(user.id).then((restored) => {
      setLoading(false);
      setUser(restored);
      history.push(reverse(routes.administrators.detail, {id: user.id}))
    }).catch(() => setLoading(false));
  }, [user, services, history, setUser, setLoading]);

  return (
    <Wrapper title="Administrator" loading={loading} user={user} crumbs={crumbs} errors={errors}>
      {user?.id ? (
        <>
          <DetailColumns columns={[

            <DetailPane
              title="User Details"
              minHeight={400}
              actions={!user.archived ? [
                {title: 'Edit User', icon: <EditIcon/>, onClick: () => toggleDialog('edit', true)},
                {title: 'Delete User', icon: <DeleteIcon/>, onClick: () => toggleDialog('delete', true)}
              ] : [
                {title: 'Restore User', icon: <RestoreFromTrashIcon/>, onClick: handleRestore},
              ]}
              details={[
                {
                  title: 'Name',
                  value: name(user)
                },
                {title: 'Email Address', value: user.email},
                {title: 'Is Active?', value: <YesNo value={user.is_active}/>},
                {title: 'Is Engineer?', value: <YesNo value={user.is_engineer}/>},
              ]}
              dialogs={[
                (props) => (
                  <FormDialog
                    {...props}
                    title="Edit User"
                    open={openDialogs.edit ?? false}
                    onClose={() => toggleDialog('edit', false)}
                    render={(props) => (
                      <UserForm
                        {...props}
                        user={user}
                        admin={true}
                        onSaved={handleSaved}/>
                    )}
                  />
                ),
                (props) => (
                  <ConfirmationDialog
                    {...props}
                    open={openDialogs.delete ?? false}
                    title="Delete"
                    message={user ? `Are you sure you want to delete ${name(user)}?` : ''}
                    onClose={confirmed => {
                      toggleDialog('delete', false);
                      if (confirmed) {
                        setLoading(true);
                        services.user.deleteUser(user.id).then(() => {
                          setLoading(false);
                          history.push(reverse(routes.administrators.index))
                        }).catch(() => setLoading(false));
                      }
                    }}
                  />
                )
              ]}
            />,

            <DetailPane
              title="Admin Details"
              details={[
                {title: 'Created', value: fullDateTime(user.created_at)},
                {title: 'Last Updated', value: fullDateTime(user.updated_at)},
                ...(user.archived ? [{title: 'Archived', value: fullDateTime(user.deleted_at)}] : [])
              ]}
            />,
          ]}/>

          <Tabs user={user} onUserUpdated={setUser} loading={loading} onLoading={setLoading}/>
        </>
      ) : null}
    </Wrapper>
  );
};

export default Administrator;
